import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import {
  Button,
  Cascader,
  Col,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { deleteData, fetchData, postData, putData } from "../store/actions/cms";
import { useState } from "react";
import { PATH_CMS as PATH, PATH_MANAGE } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import PageHeader from "../components/PageHeader";

import { queryClient } from "../store/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import objectToQueryParam from "../utils/objectToQueryParam";
import SingleUpload from "../components/SingleUpload";
import { triggerNotification } from "../store/directActions";
import CustomDrawer from "../components/hoc/CustomDrawer";
import { useLocalState } from "../hooks/useLocalStorage";
import moment from "moment";
import { getTagColor } from "../utils/getTagColor";
import socketService from "../config/ws-config";
import ModalAddSubscription from "../components/ModalAddSubscription";

const { Option } = Select;

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

const ConfigurationPage: React.FC<configurationPageProps> = ({
  lookUpData,
  countryStateData,
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>();
  const [selectedUserId, setSelectedUserId] = useState<string | null>();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [changeScanMode, SetChangeScanMode] = useState(true);
  const [showStoreInfo, toggleStoreInfo] = useState(true);

  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const { data: userCount } = useQuery({
    queryKey: ["users/store/count", filterData],
    queryFn: () =>
      queryApi<{ total: number }>(
        `users/store/count${objectToQueryParam(filterData)}`,
        "GET",
      ),
    staleTime: Infinity,
    gcTime: 0,
  });

  const [form] = Form.useForm();

  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
  };

  function filterCountryState(inputValue, path) {
    return path.some(
      (option) =>
        option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
    );
  }

  const handleEditClicked = (data: any) => {
    form.resetFields();

    toggleStoreInfo(data.role_type === 2);

    SetChangeScanMode(false);

    if (data.role_type === 2)
      form.setFieldsValue({
        ...data.store_data[0],
        ...data,
        isFormEdit: true,
        uniId: data.user_id,
        store_name: data.store_data[0].name,
        country_state: [
          data.store_data[0].country_id,
          data.store_data[0].state_id,
        ],
      });
    else
      form.setFieldsValue({ ...data, isFormEdit: true, uniId: data.user_id });

    updateDrawer(true);
  };

  const {
    mutate,
    isPending: isPendingMutation,
    // isError,
    // error,
  } = useMutation({
    mutationFn: ({ data, method, path }: any) => queryApi(path, method, data),
    onSuccess: () => {
      updateDrawer();
      queryClient.invalidateQueries({
        queryKey: ["users/store"],
      });
      queryClient.invalidateQueries({
        queryKey: ["store/payment"],
      });
    },
    onError: (a) => {
      triggerNotification({
        title: "Error",
        description: a.message,
        type: "error",
      });
    },
  });

  const { mutate: mutateSubscription, isPending: isPendingSubscription } =
    useMutation({
      mutationFn: (data: any) => queryApi(`store/subscription`, "POST", data),
      onSuccess: (data: any) => {
        if (data && data.status === false) {
          triggerNotification({
            title: "Error",
            description: data.message ?? "Subscription not created",
            type: "error",
          });
          return;
        }
        setShowSubscriptionModal(false);
        socketService.sendMessage("get-payment-status", {}, selectedUserId);
        queryClient.invalidateQueries({ queryKey: ["store/payment"] });
        queryClient.invalidateQueries({ queryKey: ["users/store"] });
      },
      onError: (a) => {
        triggerNotification({
          title: "Error",
          description: a.message,
          type: "error",
        });
      },
    });

  const handleSubscriptionSubmit = (values?: Record<string, any>) => {
    if (!values) {
      setShowSubscriptionModal(false);
      return;
    }
    if (selectedStoreId) {
      mutateSubscription({
        ...values,
        payment_receipt:
          typeof values.payment_receipt == "string"
            ? values.payment_receipt
            : values.payment_receipt?.url,
        store_id: selectedStoreId,
      });
    }
  };

  const getStateCountry = (country_id, state_id) => {
    let country_name = "";
    let state_name = "";
    let countries = lookUpData.filter((country) => country.id === country_id);

    if (countries.length > 0) {
      country_name = countries[0].name;

      let states = countries[0].children.filter(
        (state) => state.id === state_id,
      );

      if (states.length > 0) {
        state_name = states[0].name;
      }
    }

    if (state_name && country_name) return `${state_name}, ${country_name}`;
    else return "";
  };

  const onFormSubmit = (values: Record<string, any>) => {
    const payload = {
      ...values,
      store: {
        country_id:
          values.country_state && values.country_state.length > 0
            ? values.country_state[0]
            : null,
        state_id:
          values.country_state && values.country_state.length > 1
            ? values.country_state[1]
            : null,
        store_logo:
          typeof values.store_logo == "string"
            ? values.store_logo
            : values.store_logo?.url,
        zip: values.zip,
        plan_id: 1,
        city: values.city,
        street: values.street,
        lottery_retailer_no: values.lottery_retailer_no ?? 0,
        store_phone: values.mobile,
        scan_mode_reverse: values.scan_mode_reverse,
        name: values.store_name,
        store_id: values.store_id,
      },
    };

    if (values.isFormEdit) {
      const user_id = values.uniId;
      mutate({
        path: `users/${user_id}`,
        method: "PUT",
        data: { ...payload, user_id },
      });
    } else {
      mutate({
        path: `users`,
        method: "POST",
        data: { ...payload, status: 1 },
      });
    }
  };

  const onUpdateItem = (data: any, updates: any) => {
    mutate({
      path: `users/${data.user_id}`,
      method: "PUT",
      data: updates?.store
        ? { ...data, ...updates, store: updates.store }
        : { ...data, ...updates, store: data.store_data?.[0] },
    });
  };
  const columns = [
    {
      title: "Store Logo",
      dataIndex: "store_data",
      key: "store_data",
      render: (store_data) => (
        <Image
          width={60}
          height={60}
          src={`${store_data[0]?.store_logo}`.replace("original", "original")}
          fallback={
            "https://ewr1.vultrobjects.com/cdn-bs/original/e96deeed-357b-4b09-8550-93fc22b2dd23.png"
          }
        />
      ),
    },
    {
      title: "Store Name",
      dataIndex: "store_data",
      key: "store_data",
      sorter: (a, b) =>
        (a.store_data?.[0]?.name ?? "").localeCompare(
          b.store_data?.[0]?.name ?? "",
        ),
      render: (item, record, index) => <div>{item[0]?.name}</div>,
    },

    {
      title: "Contact Info",
      dataIndex: "store_data",
      key: "store_data",
      render: (item, record, index) => (
        <div>
          {record?.name}
          <br></br>
          {record?.mobile}
          <br></br>
          {record?.email}
        </div>
      ),
    },

    {
      title: "Address",
      dataIndex: "store_data",
      key: "store_data",
      render: (item, record, index) => (
        <div>
          {item[0]?.street && item[0]?.city
            ? `${item[0]?.street}, ${item[0]?.city}`
            : ""}

          <br></br>
          {record.role_type === 2
            ? getStateCountry(item[0]?.country_id, item[0]?.state_id)
            : ""}
          <br></br>
          {item[0]?.zip}
        </div>
      ),
    },
    {
      title: "Referred By",
      dataIndex: "referred_by",
      key: "referred_by",
      sorter: (a, b) =>
        (a.referred_by ?? "").localeCompare(b.referred_by ?? ""),
    },
    {
      title: "User Type",
      key: "role_type",
      dataIndex: "role_type",
      sorter: (a, b) => (a.role_type ?? 0) - (b.role_type ?? 0),
      render: (role_type) => (
        <>
          <Tag color={role_type === 1 ? "green" : "geekblue"}>
            {role_type === 1 ? "ADMIN" : "USER"}
          </Tag>
        </>
      ),
    },

    {
      title: "Date Added",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (item, record, index) => (
        <div>{moment(item).format("MMMM DD, YYYY")}</div>
      ),
    },
    // {
    //   title: "Last Paid",
    //   dataIndex: "created_at",
    //   key: "created_at",
    //   render: (item, record, index) => (
    //     <div>{moment(item).format("MMMM DD, YYYY")}</div>
    //   ),
    // },
    {
      title: "Payment",
      dataIndex: "store_data",
      align: "center",
      key: "add_payment",
      width: 150,
      filteredValue: filterData.payment_status
        ? [filterData.payment_status]
        : null,
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filterDropdown: ({ confirm }: any) => (
        <div
          style={{
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 150,
          }}
        >
          {[
            { label: "All", value: undefined },
            { label: "Active", value: "active" },
            { label: "Expired", value: "expired" },
            { label: "No payments", value: "none" },
          ].map((opt) => (
            <Button
              key={opt.label}
              size="small"
              type={
                (filterData.payment_status ?? undefined) === opt.value
                  ? "primary"
                  : "text"
              }
              style={{ textAlign: "left" }}
              onClick={() => {
                setFilterData((prev: any) => {
                  const next = { ...prev };
                  if (opt.value) next.payment_status = opt.value;
                  else delete next.payment_status;
                  return next;
                });
                confirm({ closeDropdown: true });
              }}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      ),
      render: (item, record, index) =>
        item?.[0]?.plan_active_till ? (
          <Space size="middle" direction="vertical">
            <Tag
              color={
                moment(item?.[0]?.plan_active_till).isSameOrAfter(
                  moment().startOf("day"),
                )
                  ? "green"
                  : "red"
              }
            >
              {moment(item?.[0]?.plan_active_till).isSameOrAfter(
                moment().startOf("day"),
              )
                ? `Active till ${moment(item?.[0]?.plan_active_till).format(
                    "MMMM DD, YYYY",
                  )}`
                : "EXPIRED"}
            </Tag>
          </Space>
        ) : (
          <span className="app-muted">No payments created</span>
        ),
    },

    // {
    //   title: "Email Verify",
    //   key: "email_verified",
    //   render: (item, record, index) => (
    //     <Space size="middle">
    //       <Switch
    //         checked={item.email_verified}
    //         onChange={() => onUpdateItem(item, { email_verified: !item.email_verified })}
    //       />
    //     </Space>
    //   ),
    // },
    {
      title: "Status",
      key: "status",
      render: (item, record, index) => (
        <Space size="middle">
          <Switch
            checked={item.status}
            onChange={() => onUpdateItem(item, { status: !item.status })}
          />
        </Space>
      ),
    },
    {
      title: "Show Display",
      dataIndex: "store_data",
      key: "show_display",
      render: (item, record, index) => (
        <Space size="middle">
          <Switch
            checked={!!item?.[0]?.show_display}
            onChange={() =>
              onUpdateItem(record, {
                store: {
                  ...item[0],
                  show_display: item?.[0]?.show_display == 1 ? 0 : 1,
                },
              })
            }
          />
        </Space>
      ),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "store_data",
      render: (item, record) => (
        <Space size="middle">
          <EditOutlined
            style={{ fontSize: 22 }}
            onClick={() => handleEditClicked(record)}
          />
          {/* <a onClick={() => onActionItem(record)}>Edit</a> */}
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => onUpdateItem(record, { is_deleted: true })}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined style={{ fontSize: 22, color: "red" }} />
          </Popconfirm>
          <DollarOutlined
            style={{ fontSize: 22, color: "green" }}
            onClick={() => {
              setSelectedStoreId(item?.[0]?.store_id);
              setSelectedUserId(record?.user_id);
            }}
          />
        </Space>
      ),
    },
  ];

  const paymentColumns = [
    {
      title: "Receipt",
      dataIndex: "payment_receipt",
      key: "payment_receipt",
      render: (payment_receipt: any) => (
        <Image width={60} height={60} src={payment_receipt} />
      ),
    },
    {
      title: "Bill date",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: any, b: any) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (item: any) => <div>{moment(item).format("MMMM DD, YYYY")}</div>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a: any, b: any) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (item: any) => <div>${item}</div>,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "payment_status",
      sorter: (a: any, b: any) =>
        (a.payment_status ?? "").localeCompare(b.payment_status ?? ""),
      render: (item: any) => <Tag color={getTagColor(item)}>{item}</Tag>,
    },
    {
      title: "Manually paid",
      dataIndex: "is_manual_paid",
      key: "is_manual_paid",
      render: (_: any, record: any) =>
        record?.is_manual_paid ? (
          <Tooltip title={record?.remark || "No remark"}>
            <CheckOutlined style={{ color: "green", fontSize: 18 }} />
          </Tooltip>
        ) : null,
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "subscription_id",
      render: (item: any, record: any) => (
        <Space size="middle">
          {record.payment_status === "pending" && (
            <>
              <CloseOutlined
                style={{ fontSize: 22, color: "red" }}
                onClick={() => {
                  mutate({
                    path: `store/subscription`,
                    method: "DELETE",
                    data: {
                      subscription_id: item,
                      store_id: selectedStoreId,
                    },
                  });
                  socketService.sendMessage(
                    "get-payment-status",
                    {},
                    selectedUserId,
                  );
                }}
              />
              <BellOutlined
                style={{ fontSize: 22, color: "blue" }}
                onClick={() => {
                  socketService.sendMessage(
                    "get-payment-status",
                    {},
                    selectedUserId,
                  );
                  triggerNotification({
                    title: "Payment notification sent to store",
                    type: "success",
                    description: "",
                  });
                }}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  const validateForm = (_, values) => {
    console.log("validate", values);

    if (form.getFieldsValue().isFormEdit) {
      return Promise.resolve();
    } else if (!values || values === "" || values.length < 6) {
      return Promise.reject(
        new Error("Password is mandatory and need to be greater than 6 digits"),
      );
    } else {
      return Promise.resolve();
    }
  };

  const customRender = (item: any, index: number) => {
    const store = item?.store_data?.[0];
    const planActiveTill = store?.plan_active_till;
    const isPlanActive =
      planActiveTill &&
      moment(planActiveTill).isSameOrAfter(moment().startOf("day"));
    return (
      <div
        key={item.user_id || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4 flex-1">
            <Image
              width={64}
              height={64}
              src={`${store?.store_logo}`.replace("original", "original")}
              fallback="https://ewr1.vultrobjects.com/cdn-bs/original/e96deeed-357b-4b09-8550-93fc22b2dd23.png"
              className="rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="text-lg font-semibold app-text mb-1">
                {store?.name ?? item?.name}
              </h3>
              <Tag color={item.role_type === 1 ? "green" : "geekblue"}>
                {item.role_type === 1 ? "ADMIN" : "USER"}
              </Tag>
            </div>
          </div>
          <Space size="middle">
            <EditOutlined
              style={{ fontSize: 20 }}
              onClick={() => handleEditClicked(item)}
            />
            <Popconfirm
              title="Are you sure to delete this?"
              onConfirm={() => onUpdateItem(item, { is_deleted: true })}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ fontSize: 20, color: "red" }} />
            </Popconfirm>
            <DollarOutlined
              style={{ fontSize: 20, color: "green" }}
              onClick={() => {
                setSelectedStoreId(store?.store_id);
                setSelectedUserId(item?.user_id);
              }}
            />
          </Space>
        </div>

        <div className="space-y-1 mb-4 text-sm app-muted">
          {item?.mobile && <div>{item.mobile}</div>}
          {item?.email && <div>{item.email}</div>}
          {store?.street && store?.city && (
            <div>
              {store.street}, {store.city}
            </div>
          )}
          {item.role_type === 2 &&
            getStateCountry(store?.country_id, store?.state_id) && (
              <div>{getStateCountry(store?.country_id, store?.state_id)}</div>
            )}
        </div>

        <div className="flex justify-between items-center pt-4 border-0 border-t border-solid app-border">
          <span className="text-sm app-muted">
            {moment(item.created_at).format("MMMM DD, YYYY")}
          </span>
          {!!planActiveTill && (
            <Tag color={isPlanActive ? "green" : "red"}>
              {isPlanActive ? `Active till ${planActiveTill}` : "EXPIRED"}
            </Tag>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      {/* Header and Actions */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold text-[#0c1248]">
              Expense Management
            </h2>
            <p className="app-muted mt-1">
              Track and manage all vehicle-related expenses
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button
              onClick={() => {
                form.resetFields();
                updateDrawer(true);
              }}
              className="px-4 py-2 bg-[#0c1248] text-white font-medium rounded-lg hover:bg-[#0c1248]/90 transition duration-200 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Expense
            </button>
          </div>
        </div>
      </div> */}
      <PageHeader
        title={
          <>
            Users
            {typeof userCount?.total === "number" && (
              <span className="app-muted font-normal">
                {" "}
                ({userCount.total})
              </span>
            )}
          </>
        }
        extra={[
          <Input.Search
            key="search"
            allowClear
            style={{ width: 300 }}
            placeholder="Search anything.."
            onChange={(e) =>
              setFilterData({ ...filterData, query: e.target.value })
            }
          />,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              SetChangeScanMode(true);
              form.setFieldsValue({ role_type: 2, scan_mode_reverse: 0 });
              toggleStoreInfo(true);
              updateDrawer(true);
            }}
          >
            Add
          </Button>,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        showLimitChanger
        showIndexColumn
        filterData={filterData}
        queryKey={["users/store", { type: "all" }]}
        path={`users/store`}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />

      <CustomDrawer
        title="Add New User"
        width={600}
        onClose={() => updateDrawer()}
        open={showDrawer}
        maskClosable={false}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div
            style={{
              textAlign: "right",
            }}
          >
            <Button
              onClick={() => updateDrawer()}
              style={{ marginRight: 8 }}
              disabled={isPendingMutation}
            >
              Cancel
            </Button>
            <Button
              loading={isPendingMutation}
              onClick={() => {
                form.submit();
              }}
              type="primary"
            >
              Submit
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFormSubmit}
          hideRequiredMark
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="isFormEdit" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="uniId" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="store_id" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="email_verified" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="mobile_verified" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="status" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="role_type" label="User Type">
                <Select
                  placeholder="Please choose user typr"
                  onChange={(e) => {
                    toggleStoreInfo(e === 2);
                  }}
                >
                  <Option value={1}>Admin</Option>
                  <Option value={2}>User</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter user name" }]}
              >
                <Input placeholder="Please enter user name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mobile"
                label="Contact #"
                rules={[
                  { required: true, message: "Please enter user mobile" },
                ]}
              >
                <Input placeholder="Please enter user name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, message: "Please enter user email" }]}
              >
                <Input placeholder="Please enter user name" />
              </Form.Item>
            </Col>
            {
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[{ validator: validateForm }]}
                >
                  <Input placeholder="Please enter user password" />
                </Form.Item>
              </Col>
            }

            {showStoreInfo && (
              <Row gutter={16} style={{ margin: 0, padding: "0 8px" }}>
                <Col span={24}>
                  <Form.Item
                    name="store_name"
                    label="Store Name"
                    rules={[
                      { required: true, message: "Please enter store name" },
                    ]}
                  >
                    <Input placeholder="Please enter store name" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="scan_mode_reverse"
                    label="Scan Mode"
                    rules={[
                      { required: true, message: "Please select scan mode" },
                    ]}
                  >
                    <Select
                      placeholder="Please choose scan mode"
                      disabled={!changeScanMode}
                    >
                      <Option value={0}>Normal</Option>
                      <Option value={1}>Reverse</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="street" label="Street">
                    <Input placeholder="Please enter street name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="city" label="City">
                    <Input placeholder="Please enter city name" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="country_state"
                    label="Country"
                    autocomplete="off"
                  >
                    <Cascader
                      options={countryStateData}
                      onChange={() => {}}
                      placeholder="Please select country and state"
                      showSearch={filterCountryState}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="zip" label="Zip Code">
                    <Input placeholder="Please enter zip code" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="referred_by" label="Referred By">
                    <Input placeholder="Please enter referred by name" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="store_logo"
                    label="Store Logo"

                    // getValueFromEvent={uploadFile}
                    // trigger={onFormSubmit}
                  >
                    <SingleUpload />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Row>
        </Form>
      </CustomDrawer>

      <CustomDrawer
        title="Payment History"
        width={600}
        extra={
          <Button type="primary" onClick={() => setShowSubscriptionModal(true)}>
            Create subscription
          </Button>
        }
        onClose={() => {
          setSelectedStoreId(null);
          setSelectedUserId(null);
        }}
        open={!!selectedStoreId}
        maskClosable={false}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <div className="h-full flex flex-col">
          {/* @ts-ignore */}
          <CustomPaginatedTable
            columns={paymentColumns}
            showLimitChanger
            showIndexColumn
            filterData={filterData}
            queryKey={["store/payment", { store: selectedStoreId }]}
            path={`store/payment/${selectedStoreId}`}
            tableProps={{
              sticky: true,
              tableLayout: "auto",
              scroll: { y: true, x: true },
            }}
          />
        </div>
      </CustomDrawer>

      <ModalAddSubscription
        visible={showSubscriptionModal}
        confirmLoading={isPendingSubscription}
        handleClose={handleSubscriptionSubmit}
      />
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  lookUpData: state.cms?.lookup?.country?.[0]?.children ?? [],
  countryStateData:
    state.cms?.lookup?.country?.map((country) => {
      return {
        ...country,
        value: country.id,
        label: country.name,
        children: country.children.map((state) => {
          return { ...state, value: state.id, label: state.name };
        }),
      };
    }) ?? [],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
